import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { AlertCircle, Send, Loader2, PartyPopper, Clock, MapPin, Truck } from "lucide-react";
import { ORDER_CATEGORIES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
    name: "",
    email: "",
    phone: "",
    event_date: "",
    service_type: "Pickup",
    category: "Basic Cupcakes",
    quantity: "",
    flavor: "",
    notes: "",
};

const inputCls =
    "w-full rounded-xl border border-[#F2E1E6] bg-white px-4 py-3 text-sm text-[#3D2318] placeholder:text-[#8C6A5D]/60 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-[#D4AF37]/60 transition-[box-shadow,border-color] duration-300";

const PROMISES = [
    { icon: Clock, title: "Personal reply", text: "Annabell reviews every request herself and confirms availability and pricing with you." },
    { icon: MapPin, title: "Serving our neighbors", text: "Shelton, Olympia, Lacey, and surrounding Washington areas." },
    { icon: Truck, title: "Pickup or delivery", text: "Pick up your order, or have it delivered for a small delivery fee." },
];

export default function OrderForm() {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/order-requests`, form);
            setDone(true);
            toast.success("Order request sent! We'll be in touch soon.");
        } catch (err) {
            toast.error("Something went wrong — please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="order" data-testid="order-section" className="py-16 md:py-24 lg:py-32 bg-[#FFFDF9]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">Custom Orders</p>
                            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#3D2318]">
                                Request <span className="font-script font-medium text-[#C95B7D] text-4xl sm:text-5xl align-middle">an Order</span>
                            </h2>
                            <p className="mt-4 text-base sm:text-lg text-[#6B4A3E] font-light leading-relaxed">
                                Tell us about your occasion and what you're dreaming of. We'll confirm availability and
                                pricing personally — every order gets individual attention.
                            </p>
                        </Reveal>

                        <Reveal delay={0.1}>
                            <div className="mt-8 flex gap-3 rounded-2xl border border-[#D4AF37]/40 bg-[#FAF0D7]/70 p-5" data-testid="order-notice">
                                <AlertCircle className="h-5 w-5 shrink-0 text-[#C59B27] mt-0.5" />
                                <p className="text-sm text-[#6B4A3E] leading-relaxed">
                                    <span className="font-semibold text-[#3D2318]">Please note:</span> submitting this form
                                    does not automatically confirm your order. We'll review your request and reach out to
                                    confirm availability and final pricing before anything is booked.
                                </p>
                            </div>
                        </Reveal>

                        <div className="mt-10 space-y-6">
                            {PROMISES.map((p, i) => (
                                <Reveal key={p.title} delay={0.15 + i * 0.08}>
                                    <div className="flex gap-4" data-testid={`order-promise-${i}`}>
                                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FDF0F4] border border-[#F2E1E6]">
                                            <p.icon className="h-5 w-5 text-[#C95B7D]" />
                                        </span>
                                        <div>
                                            <h3 className="font-display text-lg font-semibold text-[#3D2318]">{p.title}</h3>
                                            <p className="mt-1 text-sm text-[#6B4A3E] leading-relaxed">{p.text}</p>
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <Reveal delay={0.1}>
                            {done ? (
                                <div className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-[2rem] border border-[#F2E1E6] bg-white p-10 text-center shadow-[0_20px_50px_rgba(61,35,24,0.08)]" data-testid="order-success">
                                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#FDF0F4]">
                                        <PartyPopper className="h-8 w-8 text-[#C95B7D]" />
                                    </span>
                                    <h3 className="mt-6 font-display text-2xl sm:text-3xl font-bold text-[#3D2318]">
                                        Request received!
                                    </h3>
                                    <p className="mt-3 max-w-md text-sm sm:text-base text-[#6B4A3E] leading-relaxed">
                                        Thank you! We've sent a confirmation to your email. Annabell will review your request and reach out soon to confirm
                                        availability and pricing. Nothing is booked until we've chatted.
                                    </p>
                                    <button
                                        data-testid="order-another-button"
                                        onClick={() => { setForm(initial); setDone(false); }}
                                        className="mt-8 rounded-full border border-[#3D2318]/15 px-6 py-3 text-sm font-semibold text-[#3D2318] hover:border-[#C59B27]/60 hover:bg-[#FAF0D7]/70 transition-[background-color,border-color] duration-300"
                                    >
                                        Send another request
                                    </button>
                                </div>
                            ) : (
                                <form
                                    onSubmit={submit}
                                    data-testid="order-form"
                                    className="rounded-[2rem] border border-[#F2E1E6] bg-white p-8 sm:p-10 shadow-[0_20px_50px_rgba(61,35,24,0.08)]"
                                >
                                    <div className="grid sm:grid-cols-2 gap-5">
                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-[#8C6A5D]">Full Name *</label>
                                            <input required data-testid="order-name-input" value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                                        </div>
                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-[#8C6A5D]">Email *</label>
                                            <input required type="email" data-testid="order-email-input" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
                                        </div>
                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-[#8C6A5D]">Phone</label>
                                            <input data-testid="order-phone-input" value={form.phone} onChange={set("phone")} placeholder="(360) 555-0123" className={inputCls} />
                                        </div>
                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-[#8C6A5D]">Event Date</label>
                                            <input type="date" data-testid="order-date-input" value={form.event_date} onChange={set("event_date")} className={inputCls} />
                                        </div>
                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-[#8C6A5D]">Pickup or Delivery</label>
                                            <select data-testid="order-service-select" value={form.service_type} onChange={set("service_type")} className={inputCls}>
                                                <option>Pickup</option>
                                                <option>Delivery (small fee)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-[#8C6A5D]">Treat Category</label>
                                            <select data-testid="order-category-select" value={form.category} onChange={set("category")} className={inputCls}>
                                                {ORDER_CATEGORIES.map((c) => (
                                                    <option key={c}>{c}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-[#8C6A5D]">Quantity Needed</label>
                                            <input data-testid="order-quantity-input" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 2 dozen cupcakes, one 8-inch cake" className={inputCls} />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-[#8C6A5D]">Flavor You'd Like</label>
                                            <input data-testid="order-flavor-input" value={form.flavor} onChange={set("flavor")} placeholder="e.g. Carrot cake, lemon bar cupcakes — flavor requests welcome!" className={inputCls} />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.15em] text-[#8C6A5D]">Design Ideas &amp; Details</label>
                                            <textarea
                                                rows={4}
                                                data-testid="order-notes-input"
                                                value={form.notes}
                                                onChange={set("notes")}
                                                placeholder="Tell us about your occasion, colors, theme — anything that helps us dream it up!"
                                                className={inputCls}
                                            />
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        data-testid="order-submit-button"
                                        className="group mt-7 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#C95B7D] px-8 py-4 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(201,91,125,0.4)] hover:bg-[#b44a6b] hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0 transition-[transform,background-color,opacity] duration-300"
                                    >
                                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                                        {loading ? "Sending…" : "Send Order Request"}
                                    </button>
                                </form>
                            )}
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
